'use client';

import { cloneElement, useRef, useState } from 'react';

import {
    arrow as arrowMiddleware,
    autoUpdate,
    flip,
    FloatingPortal,
    offset as offsetMiddleware,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole
} from '@floating-ui/react';

import { classNames } from '@/utils';

import type { TooltipProps } from '@/components/base/Tooltip/types';

import {
    TOOLTIP_ARROW_STYLE,
    TOOLTIP_ARROW_VARIANT,
    TOOLTIP_STYLE
} from '@/components/base/Tooltip/styles';

const OPPOSITE_SIDE = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
} as const;

export const TooltipFloating = ({
    children,
    content,
    placement = 'bottom',
    arrow = true,
    variant = 'default',
    arrowVariant = 'default',
    tooltipAdditionalClasses,
    disabled = false,
    interactive = false,
    appendTo,
    offset
}: TooltipProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef<HTMLDivElement>(null);

    const offsetValue = offset ? { mainAxis: offset[1], crossAxis: offset[0] } : undefined;

    const {
        refs,
        floatingStyles,
        context,
        placement: currentPlacement,
        middlewareData
    } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetValue ? offsetMiddleware(offsetValue) : offsetMiddleware(5),
            flip(),
            shift({ padding: 5 }),
            ...(arrow ? [arrowMiddleware({ element: arrowRef, padding: 5 })] : [])
        ]
    });

    const hover = useHover(context, {
        move: false,
        enabled: !disabled,
        handleClose: interactive ? safePolygon() : undefined
    });
    const focus = useFocus(context, { enabled: !disabled });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

    const childRef = (children as any).ref;
    const mergedRef = useMergeRefs([refs.setReference, childRef]);

    const referenceElement = cloneElement(children, {
        ...getReferenceProps(),
        ref: mergedRef
    } as any);

    const side = currentPlacement.split('-')[0] as keyof typeof OPPOSITE_SIDE;
    const staticSide = OPPOSITE_SIDE[side];
    const arrowData = middlewareData.arrow;

    const usePortal = appendTo !== 'parent';

    const floatingContent =
        isOpen && content ? (
            <div
                ref={refs.setFloating}
                style={{
                    ...floatingStyles,
                    zIndex: 99999,
                    pointerEvents: interactive ? 'auto' : 'none',
                    width: 'max-content'
                }}
                {...getFloatingProps()}
                className={classNames(TOOLTIP_STYLE[variant], tooltipAdditionalClasses)}>
                {content}
                {arrow ? (
                    <div
                        ref={arrowRef}
                        data-testid="tooltip-arrow"
                        className={TOOLTIP_ARROW_VARIANT[arrowVariant]}
                        style={{
                            position: 'absolute',
                            ...TOOLTIP_ARROW_STYLE[side],
                            left: arrowData?.x != null ? `${arrowData.x}px` : '',
                            top: arrowData?.y != null ? `${arrowData.y}px` : '',
                            [staticSide]: '-6px'
                        }}
                    />
                ) : null}
            </div>
        ) : null;

    return (
        <>
            {referenceElement}
            {usePortal ? <FloatingPortal>{floatingContent}</FloatingPortal> : floatingContent}
        </>
    );
};
